import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Environment, ENVIRONMENT } from '@ld.consumer-portal/models';
import { delay, of, throwError } from 'rxjs';
import {
  contactInfoMock,
  drawEligibilityMock,
  drawFundsMock,
  getScenarioForEndpoint,
  helocAutopayStatusMock,
  helocLoanInfoMock,
  helocPendingDrawRecordsMock,
  loanFlagsMock,
  loanSummaryMock,
  mlaBorrowerInfoMock,
  mlaDashboardMock,
  originationApplicationsMock,
  payoffQuoteMock,
  tcpaDetailsMock,
  updateContactInfoMock,
  updateLoanPaperlessSettingsMock,
  updateTcpaConsentMock,
} from '../mock';
import { mockAuthUserProfile, mlaAuthTokenMockResponses, ropcAuthMockResponses } from '../mock/auth.mock';
import {
  mlaCheckEmailStatusMock,
  mlaSendPasswordResetMock,
  mlaUpdatePasswordWithTokenMock,
} from '../mock/mla-password.mock';
import {
  autopayBankAccountsMock,
  autopayCancelMockResponses,
  autopayDeletedPaymentsMock,
  autopayExistingPaymentsMock,
  autopaySetupMockResponses,
} from '../mock/autopay.mock';
import {
  customerIdentityAuthCallbackMock,
  customerIdentityAuthenticationMethodsMock,
  customerIdentityCreateEmailAuthMethodMock,
  customerIdentityCreatePhoneAuthMethodMock,
  customerIdentityLegacyUserLookupMock,
  customerIdentityCreateUserProfilePropertiesMock,
  customerIdentityNativeSignupContinueMock,
  customerIdentityNativeSignupStartMock,
  customerIdentityStartMigrationMock,
  customerIdentityTenantConfigMock,
  customerIdentityVerifyEmailMock,
} from '../mock/customer-identity.mock';
import { documentsMock, downloadDocumentMock } from '../mock/documents.mock';
import { loanResponsesMock } from '../mock/loan.mock';
import {
  getMfaRememberDeviceStatus,
  mfaMockResponses,
  mfaVerifyMockResponses,
  setMfaRememberDevice,
} from '../mock/mfa.mock';
import { originationContactsScenarios, originationMessageMockState, originationMessagesScenarios, originationMetadataScenarios } from '../mock/origination-messaging.mock';
import {
  loanDetailScenarios,
  loanProgressScenarios,
  loanTeamScenarios,
  originationDocumentListScenarios,
  originationTodosScenarios,
} from '../mock/origination.mock';
import {
  paymentHistoryMock,
  paymentPageDataMock,
  submitPaymentMock,
  validateRoutingNumberMock,
} from '../mock/payment-history.mock';
import {
  createAssetScenarios,
  createBorrowerScenarios,
  createIncomeScenarios,
  createLoanScenarios,
  deleteAssetScenarios,
  deleteIncomeScenarios,
  updateAssetScenarios,
  updateBorrowerScenarios,
  updateIncomeScenarios,
  updateLoanScenarios,
} from '../mock/purchase-application.mock';
import {
  deleteMessageMock,
  markMessageReadMock,
  secureMessagesMock,
  sendMessageMock,
} from '../mock/secure-message.mock';
import { loanLookupMock, registerLoanMock, registerUserMock, userProfileMock, validateLoanMock } from '../mock/user-profile.mock';

export const mockDataInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const env = inject(ENVIRONMENT);

  if (env.production) {
    return next(req);
  }

  if (!env.mockConfig?.enabled) {
    return next(req);
  }

  // Ensure MLA session values are present so the origination applications flow works in mock mode.
  // Without these, ensureMlaSession() bails out before any HTTP call reaches the mock interceptor.
  if (!sessionStorage.getItem('eps_token')) {
    sessionStorage.setItem('eps_token', 'mock-eps-token-12345');
    sessionStorage.setItem('mla_userId', 'mock-mla-user-001');
    sessionStorage.setItem('mla_user_exists', 'true');
  }

  const mockResponse = getMockResponse(req, env);

  if (!mockResponse) {
    const isStaticAsset =
      req.url.includes('/assets/') ||
      req.url.includes('.json') ||
      req.url.includes('.png') ||
      req.url.includes('.svg') ||
      req.url.includes('.css') ||
      req.url.includes('.js') ||
      req.url.includes('hot-update') ||
      req.url.includes('sockjs-node') ||
      req.url.includes('webpack') ||
      req.url.includes('favicon');

    if (isStaticAsset) {
      return next(req);
    }

    const errorMsg =
      `[MockInterceptor] ❌ UNMOCKED API CALL: ${req.method} ${req.url}\n` +
      `Add a mock for this endpoint in mock-data.interceptor.ts`;
    // eslint-disable-next-line no-console
    console.error(errorMsg);

    throw new Error(errorMsg);
  }

  const { response, delayMs, scenario, status } = mockResponse;

  const scenarioInfo = scenario ? ` → scenario: ${scenario}` : '';
  // eslint-disable-next-line no-console
  console.log(`[MockInterceptor] Mocking ${req.method} ${req.url}${scenarioInfo}`);

  const responseType = req.responseType;
  const body = responseType === 'text' && typeof response !== 'string' ? JSON.stringify(response) : response;

  if (status >= 400) {
    const error$ = throwError(() => new HttpErrorResponse({ status, error: body, url: req.url }));
    return delayMs ? error$.pipe(delay(delayMs)) : error$;
  }

  const response$ = of(
    new HttpResponse({
      status,
      body,
    }),
  );

  return delayMs ? response$.pipe(delay(delayMs)) : response$;
};

function getEndpointScenario(endpointName: string, env: Environment, defaultScenario: string): string {
  const endpointConfig = env.mockConfig?.endpoints?.[endpointName];
  return (
    getScenarioForEndpoint(endpointName, env.mockConfig?.activeScenario, endpointConfig?.scenario) || defaultScenario
  );
}

function isEndpointEnabled(endpointName: string, env: Environment): boolean {
  const endpointConfig = env.mockConfig?.endpoints?.[endpointName];
  if (endpointConfig !== undefined) {
    return endpointConfig.enabled;
  }
  return !!env.mockConfig?.activeScenario;
}

function getEndpointDelay(endpointName: string, env: Environment): number {
  return env.mockConfig?.endpoints?.[endpointName]?.delay || 0;
}

function buildMockResult(
  endpointName: string,
  scenario: string,
  response: unknown,
  delayMs: number,
  status = 200,
): { response: unknown; delayMs: number; endpointName: string; scenario: string; status: number } {
  return { response, delayMs, endpointName, scenario, status };
}

function getMockResponse(
  req: HttpRequest<unknown>,
  env: Environment,
): { response: unknown; delayMs: number; endpointName: string; scenario: string; status: number } | null {
  const url = req.url;

  if (url.includes('/oauth2/v2.0/token') && req.method === 'POST') {
    const ropcEnabled = env.mockConfig?.endpoints?.['RopcAuth']?.enabled !== false;
    if (ropcEnabled) {
      const scenario = getEndpointScenario('RopcAuth', env, 'success');
      const response = ropcAuthMockResponses[scenario] || ropcAuthMockResponses['success'];

      if ('error' in response) {
        return buildMockResult('RopcAuth', scenario, response, getEndpointDelay('RopcAuth', env));
      }

      return buildMockResult('RopcAuth', scenario, response, getEndpointDelay('RopcAuth', env));
    }
  }

  if (url.includes('/api/v1/authentication/token') && req.method === 'POST') {
    const mlaAuthEnabled = env.mockConfig?.endpoints?.['MlaAuthToken']?.enabled !== false;
    if (mlaAuthEnabled) {
      const endpointConfig = env.mockConfig?.endpoints?.['MlaAuthToken'];
      // eslint-disable-next-line no-console
      console.log('[MockInterceptor] MlaAuthToken config:', endpointConfig);
      // eslint-disable-next-line no-console
      console.log('[MockInterceptor] activeScenario:', env.mockConfig?.activeScenario);
      const scenario = getEndpointScenario('MlaAuthToken', env, 'success');
      // eslint-disable-next-line no-console
      console.log('[MockInterceptor] MlaAuthToken resolved scenario:', scenario);
      const response = mlaAuthTokenMockResponses[scenario] || mlaAuthTokenMockResponses['success'];
      return buildMockResult('MlaAuthToken', scenario, response, getEndpointDelay('MlaAuthToken', env));
    }
  }

  if (url.includes('/api/v1/borrower/verify') && req.method === 'POST') {
    const borrowerVerifyEnabled = env.mockConfig?.endpoints?.['BorrowerVerify']?.enabled !== false;
    if (borrowerVerifyEnabled) {
      const urlParams = new URL(url).searchParams;
      const borrowerToken = urlParams.get('borrowerToken');
      const lastFourSsn = urlParams.get('lastFourSsn');

      const isValidSsn = lastFourSsn === '1234';

      const response = {
        success: true,
        data: {
          success: true,
          isVerified: isValidSsn,
          borrowerToken: borrowerToken || '',
          message: isValidSsn ? undefined : 'The SSN digits you entered do not match our records.',
        },
      };

      return buildMockResult(
        'BorrowerVerify',
        isValidSsn ? 'success' : 'ssn-mismatch',
        response,
        getEndpointDelay('BorrowerVerify', env),
      );
    }
  }

  if (url.includes('/api/TenantConfiguration') && req.method === 'GET') {
    const ciamEnabled = env.mockConfig?.endpoints?.['CiamTenantConfig']?.enabled !== false;
    if (ciamEnabled) {
      const scenario = getEndpointScenario('CiamTenantConfig', env, 'success');
      const response = customerIdentityTenantConfigMock[scenario] || customerIdentityTenantConfigMock['success'];
      return buildMockResult('CiamTenantConfig', scenario, response, getEndpointDelay('CiamTenantConfig', env));
    }
  }

  if (url.includes('/api/VerifyEmail') && req.method === 'POST') {
    const ciamEnabled = env.mockConfig?.endpoints?.['CiamVerifyEmail']?.enabled !== false;
    if (ciamEnabled) {
      const scenario = getEndpointScenario('CiamVerifyEmail', env, 'success');
      const response = customerIdentityVerifyEmailMock[scenario] || customerIdentityVerifyEmailMock['success'];
      return buildMockResult('CiamVerifyEmail', scenario, response, getEndpointDelay('CiamVerifyEmail', env));
    }
  }

  if (url.includes('/api/LegacyUserLookup') && req.method === 'POST') {
    const ciamEnabled = env.mockConfig?.endpoints?.['CiamLegacyUserLookup']?.enabled !== false;
    if (ciamEnabled) {
      const scenario = getEndpointScenario('CiamLegacyUserLookup', env, 'mla-user');
      const response =
        customerIdentityLegacyUserLookupMock[scenario] || customerIdentityLegacyUserLookupMock['mla-user'];
      return buildMockResult('CiamLegacyUserLookup', scenario, response, getEndpointDelay('CiamLegacyUserLookup', env));
    }
  }

  if (url.includes('/api/StartMigration') && req.method === 'POST') {
    const ciamEnabled = env.mockConfig?.endpoints?.['CiamStartMigration']?.enabled !== false;
    if (ciamEnabled) {
      const scenario = getEndpointScenario('CiamStartMigration', env, 'success');
      const response = customerIdentityStartMigrationMock[scenario] || customerIdentityStartMigrationMock['success'];
      return buildMockResult('CiamStartMigration', scenario, response, getEndpointDelay('CiamStartMigration', env));
    }
  }

  if (url.includes('/api/AuthCallback') && req.method === 'POST') {
    const ciamEnabled = env.mockConfig?.endpoints?.['CiamAuthCallback']?.enabled !== false;
    if (ciamEnabled) {
      const scenario = getEndpointScenario('CiamAuthCallback', env, 'success');
      const response = customerIdentityAuthCallbackMock[scenario] || customerIdentityAuthCallbackMock['success'];
      return buildMockResult('CiamAuthCallback', scenario, response, getEndpointDelay('CiamAuthCallback', env));
    }
  }

  if (url.includes('/api/AuthenticationMethods') && !url.includes('Phone') && !url.includes('Email') && req.method === 'GET') {
    const ciamEnabled = env.mockConfig?.endpoints?.['CiamGetAuthenticationMethods']?.enabled !== false;
    if (ciamEnabled) {
      const scenario = getEndpointScenario('CiamGetAuthenticationMethods', env, 'success');
      const response =
        customerIdentityAuthenticationMethodsMock[scenario] || customerIdentityAuthenticationMethodsMock['success'];
      return buildMockResult(
        'CiamGetAuthenticationMethods',
        scenario,
        response,
        getEndpointDelay('CiamGetAuthenticationMethods', env),
      );
    }
  }

  if (url.includes('/api/PhoneAuthenticationMethod') && req.method === 'POST') {
    const ciamEnabled = env.mockConfig?.endpoints?.['CiamCreatePhoneAuthMethod']?.enabled !== false;
    if (ciamEnabled) {
      const scenario = getEndpointScenario('CiamCreatePhoneAuthMethod', env, 'success');
      const response =
        customerIdentityCreatePhoneAuthMethodMock[scenario] || customerIdentityCreatePhoneAuthMethodMock['success'];
      return buildMockResult(
        'CiamCreatePhoneAuthMethod',
        scenario,
        response,
        getEndpointDelay('CiamCreatePhoneAuthMethod', env),
      );
    }
  }

  if (url.includes('/api/EmailAuthenticationMethod') && req.method === 'POST') {
    const ciamEnabled = env.mockConfig?.endpoints?.['CiamCreateEmailAuthMethod']?.enabled !== false;
    if (ciamEnabled) {
      const scenario = getEndpointScenario('CiamCreateEmailAuthMethod', env, 'success');
      const response =
        customerIdentityCreateEmailAuthMethodMock[scenario] || customerIdentityCreateEmailAuthMethodMock['success'];
      return buildMockResult(
        'CiamCreateEmailAuthMethod',
        scenario,
        response,
        getEndpointDelay('CiamCreateEmailAuthMethod', env),
      );
    }
  }

  if (url.includes('/api/NativeSignupStart') && req.method === 'POST') {
    if (isEndpointEnabled('NativeSignupStart', env)) {
      const scenario = getEndpointScenario('NativeSignupStart', env, 'success');
      const response =
        customerIdentityNativeSignupStartMock[scenario] || customerIdentityNativeSignupStartMock['success'];
      return buildMockResult('NativeSignupStart', scenario, response, getEndpointDelay('NativeSignupStart', env));
    }
  }

  if (url.includes('/api/NativeSignupContinue') && req.method === 'POST') {
    if (isEndpointEnabled('NativeSignupContinue', env)) {
      const body = req.body as { otp?: string; password?: string } | null;
      let scenario: string;
      if (body?.otp) {
        scenario = getEndpointScenario('NativeSignupContinue', env, 'otp-verified');
      } else if (body?.password) {
        scenario = getEndpointScenario('NativeSignupContinue', env, 'complete');
      } else {
        scenario = getEndpointScenario('NativeSignupContinue', env, 'complete');
      }
      const response =
        customerIdentityNativeSignupContinueMock[scenario] || customerIdentityNativeSignupContinueMock['complete'];
      return buildMockResult(
        'NativeSignupContinue',
        scenario,
        response,
        getEndpointDelay('NativeSignupContinue', env),
      );
    }
  }

  if (url.includes('LoanLookup') && req.method === 'POST' && isEndpointEnabled('LoanLookup', env)) {
    const scenario = getEndpointScenario('LoanLookup', env, 'found');
    const response = loanLookupMock[scenario] || loanLookupMock['found'];
    return buildMockResult('LoanLookup', scenario, response, getEndpointDelay('LoanLookup', env));
  }

  if (url.endsWith('LDProfileDomain/UserProfile') && req.method === 'POST' && isEndpointEnabled('RegisterUser', env)) {
    const scenario = getEndpointScenario('RegisterUser', env, 'success');
    const response = registerUserMock[scenario] || registerUserMock['success'];
    return buildMockResult('RegisterUser', scenario, response, getEndpointDelay('RegisterUser', env));
  }

  if (url.includes('UserProfileProperties') && req.method === 'POST' && isEndpointEnabled('UserProfileProperties', env)) {
    const scenario = getEndpointScenario('UserProfileProperties', env, 'success');
    const response = customerIdentityCreateUserProfilePropertiesMock[scenario] || customerIdentityCreateUserProfilePropertiesMock['success'];
    return buildMockResult('UserProfileProperties', scenario, response, getEndpointDelay('UserProfileProperties', env));
  }

  if (url.includes('ProcessLoginMFA') && isEndpointEnabled('ProcessLoginMFA', env)) {
    const rememberedStatus = getMfaRememberDeviceStatus();
    if (rememberedStatus === 'valid-device') {
      const response = mfaMockResponses['valid-device'];
      return buildMockResult('ProcessLoginMFA', 'valid-device', response, getEndpointDelay('ProcessLoginMFA', env));
    }
    const scenario = getEndpointScenario('ProcessLoginMFA', env, 'mfa-required');
    const response = mfaMockResponses[scenario] || mfaMockResponses['mfa-required'];
    return buildMockResult('ProcessLoginMFA', scenario, response, getEndpointDelay('ProcessLoginMFA', env));
  }

  if (url.includes('SendLoginMFACode') && isEndpointEnabled('SendLoginMFACode', env)) {
    return buildMockResult(
      'SendLoginMFACode',
      'success',
      { isSuccess: true, message: 'Code sent' },
      getEndpointDelay('SendLoginMFACode', env),
    );
  }

  if (url.includes('VerifyLoginMFACode') && isEndpointEnabled('VerifyLoginMFACode', env)) {
    const scenario = getEndpointScenario('VerifyLoginMFACode', env, 'success');
    const response =
      mfaVerifyMockResponses[scenario as keyof typeof mfaVerifyMockResponses] || mfaVerifyMockResponses['success'];

    const body = req.body as { rememberMe?: boolean; rememberDevice?: boolean } | null;
    if (body && (body.rememberMe === true || body.rememberDevice === true) && scenario === 'success') {
      setMfaRememberDevice(true);
    }

    return buildMockResult('VerifyLoginMFACode', scenario, response, getEndpointDelay('VerifyLoginMFACode', env));
  }

  if (url.includes('validateLoan') && req.method === 'GET' && isEndpointEnabled('ValidateLoan', env)) {
    const scenario = getEndpointScenario('ValidateLoan', env, 'success');
    const response = validateLoanMock[scenario as keyof typeof validateLoanMock] || validateLoanMock['success'];
    return buildMockResult('ValidateLoan', scenario, response, getEndpointDelay('ValidateLoan', env));
  }

  if (
    url.includes('UserProfile') &&
    !url.includes('MFA') &&
    req.method === 'GET' &&
    isEndpointEnabled('GetUserProfile', env)
  ) {
    const scenario = getEndpointScenario('GetUserProfile', env, 'multiple-loans');
    const response = userProfileMock[scenario as keyof typeof userProfileMock] || userProfileMock['multiple-loans'];
    return buildMockResult('GetUserProfile', scenario, response, getEndpointDelay('GetUserProfile', env));
  }

  if (
    (url.includes('RegisterLoan') || url.includes('RegisterUserProperty')) &&
    req.method === 'POST' &&
    isEndpointEnabled('RegisterLoan', env)
  ) {
    const scenario = getEndpointScenario('RegisterLoan', env, 'success');
    const response = registerLoanMock[scenario as keyof typeof registerLoanMock] || registerLoanMock['success'];
    return buildMockResult('RegisterLoan', scenario, response, getEndpointDelay('RegisterLoan', env));
  }

  if (url.includes('GetLoans') && req.method === 'POST' && isEndpointEnabled('GetLoans', env)) {
    const scenario = getEndpointScenario('GetLoans', env, 'multiple-loans');
    const response =
      loanResponsesMock[scenario as keyof typeof loanResponsesMock] || loanResponsesMock['multiple-loans'];
    return buildMockResult('GetLoans', scenario, response, getEndpointDelay('GetLoans', env));
  }

  if (url.includes('LoanFlags') && isEndpointEnabled('GetLoanFlags', env)) {
    const scenario = getEndpointScenario('GetLoanFlags', env, 'all-features');
    const response = loanFlagsMock[scenario as keyof typeof loanFlagsMock] || loanFlagsMock['all-features'];
    return buildMockResult('GetLoanFlags', scenario, response, getEndpointDelay('GetLoanFlags', env));
  }

  if (url.includes('GetLoanSummary') && req.method === 'GET' && isEndpointEnabled('GetLoanSummary', env)) {
    const loanNumberMatch = url.match(/GetLoanSummary\/(\d+)/);
    let scenario = 'default';

    if (loanNumberMatch) {
      const loanNumber = loanNumberMatch[1];
      if (loanNumber.includes('9876543')) scenario = 'heloc';
      else if (loanNumber.includes('5555555')) scenario = 'home-equity';
    }

    const response = loanSummaryMock[scenario] || loanSummaryMock['default'];
    return buildMockResult('GetLoanSummary', scenario, response, getEndpointDelay('GetLoanSummary', env));
  }

  if (url.includes('BankValidation') && req.method === 'GET' && isEndpointEnabled('ValidateRoutingNumber', env)) {
    const scenario = getEndpointScenario('ValidateRoutingNumber', env, 'valid');
    const response =
      validateRoutingNumberMock[scenario as keyof typeof validateRoutingNumberMock] ||
      validateRoutingNumberMock['valid'];
    return buildMockResult('ValidateRoutingNumber', scenario, response, getEndpointDelay('ValidateRoutingNumber', env));
  }

  if (
    url.includes('PaymentProfile') &&
    !url.includes('BankValidation') &&
    req.method === 'POST' &&
    isEndpointEnabled('GetBankAccounts', env)
  ) {
    const body = req.body as Record<string, unknown>;
    const profileDetails = body?.['paymentProfileDetails'] as Record<string, string> | undefined;
    const accountNumber = profileDetails?.['accountNumber'] ?? '';
    const existingLastFour = ['4567', '8901', '2345'];
    if (existingLastFour.includes(accountNumber.slice(-4))) {
      const errorResponse = { resultCode: '1003', errorMessage: 'Duplicate bank account' };
      return buildMockResult(
        'AddBankAccount',
        'duplicate',
        errorResponse,
        getEndpointDelay('GetBankAccounts', env),
        400,
      );
    }
    const response = {
      paymentProfileId: 'mock-new-profile-' + Date.now(),
      accountVerificationStatus: 'Valid',
    };
    return buildMockResult('AddBankAccount', 'success', response, getEndpointDelay('GetBankAccounts', env));
  }

  if (
    url.includes('PaymentProfile') &&
    !url.includes('BankValidation') &&
    req.method === 'GET' &&
    isEndpointEnabled('GetBankAccounts', env)
  ) {
    const scenario = getEndpointScenario('GetBankAccounts', env, 'multiple-accounts');
    let response =
      autopayBankAccountsMock[scenario as keyof typeof autopayBankAccountsMock] ||
      autopayBankAccountsMock['multiple-accounts'];

    const loanTypeParam = req.params.get('loanType');
    if (loanTypeParam) {
      response = response.filter((account) => String(account.loanType) === loanTypeParam);
    }

    return buildMockResult('GetBankAccounts', scenario, response, getEndpointDelay('GetBankAccounts', env));
  }

  if (
    url.includes('/Payments/') &&
    url.includes('encryptionValue') &&
    !url.includes('/Pending') &&
    req.method === 'GET' &&
    isEndpointEnabled('GetPaymentPageData', env)
  ) {
    const scenario = getEndpointScenario('GetPaymentPageData', env, 'default');
    const response =
      paymentPageDataMock[scenario as keyof typeof paymentPageDataMock] || paymentPageDataMock['default'];
    return buildMockResult('GetPaymentPageData', scenario, response, getEndpointDelay('GetPaymentPageData', env));
  }

  if (
    url.includes('/Payments/') &&
    url.includes('encryptionValue') &&
    req.method === 'GET' &&
    isEndpointEnabled('GetPaymentHistory', env)
  ) {
    const scenario = getEndpointScenario('GetPaymentHistory', env, 'with-history');
    const response =
      paymentHistoryMock[scenario as keyof typeof paymentHistoryMock] || paymentHistoryMock['with-history'];
    return buildMockResult('GetPaymentHistory', scenario, response, getEndpointDelay('GetPaymentHistory', env));
  }

  if (url.includes('SubmitPayment') && req.method === 'POST' && isEndpointEnabled('SubmitPayment', env)) {
    const scenario = getEndpointScenario('SubmitPayment', env, 'success');
    const response = submitPaymentMock[scenario as keyof typeof submitPaymentMock] || submitPaymentMock['success'];
    return buildMockResult('SubmitPayment', scenario, response, getEndpointDelay('SubmitPayment', env));
  }

  if (url.includes('ValidateRoutingNumber') && isEndpointEnabled('ValidateRoutingNumber', env)) {
    const scenario = getEndpointScenario('ValidateRoutingNumber', env, 'valid');
    const response =
      validateRoutingNumberMock[scenario as keyof typeof validateRoutingNumberMock] ||
      validateRoutingNumberMock['valid'];
    return buildMockResult('ValidateRoutingNumber', scenario, response, getEndpointDelay('ValidateRoutingNumber', env));
  }

  if (url.match(/\/Pending\/?$/) && req.method === 'GET' && isEndpointEnabled('GetAutopayPayments', env)) {
    const isNoAutopayLoan = url.includes('/1111111111/');
    const scenario = isNoAutopayLoan ? 'no-autopay' : getEndpointScenario('GetAutopayPayments', env, 'no-autopay');
    const response =
      autopayExistingPaymentsMock[scenario as keyof typeof autopayExistingPaymentsMock] ||
      autopayExistingPaymentsMock['no-autopay'];
    return buildMockResult('GetAutopayPayments', scenario, response, getEndpointDelay('GetAutopayPayments', env));
  }

  if (
    url.includes('/Payments/') &&
    req.method === 'POST' &&
    !url.includes('encryptionValue') &&
    isEndpointEnabled('SetupAutopay', env)
  ) {
    const scenario = getEndpointScenario('SetupAutopay', env, 'success');
    const response =
      autopaySetupMockResponses[scenario as keyof typeof autopaySetupMockResponses] ||
      autopaySetupMockResponses['success'];
    const status = response && 'isSuccess' in response && !response.isSuccess ? 400 : 200;
    return buildMockResult('SetupAutopay', scenario, response, getEndpointDelay('SetupAutopay', env), status);
  }

  if (url.includes('/Payments/') && req.method === 'DELETE' && isEndpointEnabled('CancelAutopay', env)) {
    const scenario = getEndpointScenario('CancelAutopay', env, 'success');
    const response =
      autopayCancelMockResponses[scenario as keyof typeof autopayCancelMockResponses] ||
      autopayCancelMockResponses['success'];
    return buildMockResult('CancelAutopay', scenario, response, getEndpointDelay('CancelAutopay', env));
  }

  if (url.includes('/PaymentStatus/') && req.method === 'GET' && isEndpointEnabled('GetDeletedPayments', env)) {
    const scenario = getEndpointScenario('GetDeletedPayments', env, 'no-deleted');
    const response =
      autopayDeletedPaymentsMock[scenario as keyof typeof autopayDeletedPaymentsMock] ||
      autopayDeletedPaymentsMock['no-deleted'];
    return buildMockResult('GetDeletedPayments', scenario, response, getEndpointDelay('GetDeletedPayments', env));
  }

  if ((url.includes('HelocLoanInfo') || url.includes('Heloc/LoanInfo')) && isEndpointEnabled('GetHelocLoanInfo', env)) {
    const scenario = getEndpointScenario('GetHelocLoanInfo', env, 'draw-eligible');
    const response =
      helocLoanInfoMock[scenario as keyof typeof helocLoanInfoMock] || helocLoanInfoMock['draw-eligible'];
    return buildMockResult('GetHelocLoanInfo', scenario, response, getEndpointDelay('GetHelocLoanInfo', env));
  }

  if (url.includes('DrawEligibility') && req.method === 'GET' && isEndpointEnabled('GetDrawEligibility', env)) {
    const scenario = getEndpointScenario('GetDrawEligibility', env, 'eligible');
    const response =
      drawEligibilityMock[scenario as keyof typeof drawEligibilityMock] || drawEligibilityMock['eligible'];
    return buildMockResult('GetDrawEligibility', scenario, response, getEndpointDelay('GetDrawEligibility', env));
  }

  if (
    url.includes('HelocDraws') &&
    !url.includes('Initiate') &&
    req.method === 'GET' &&
    isEndpointEnabled('GetHelocDrawRecords', env)
  ) {
    const scenario = getEndpointScenario('GetHelocDrawRecords', env, 'single-pending');
    const response =
      helocPendingDrawRecordsMock[scenario as keyof typeof helocPendingDrawRecordsMock] ||
      helocPendingDrawRecordsMock['single-pending'];
    return buildMockResult('GetHelocDrawRecords', scenario, response, getEndpointDelay('GetHelocDrawRecords', env));
  }

  if (
    url.includes('HelocDraws') &&
    !url.includes('Initiate') &&
    req.method === 'POST' &&
    isEndpointEnabled('SaveDrawRecord', env)
  ) {
    const scenario = getEndpointScenario('SaveDrawRecord', env, 'success');
    const response = drawFundsMock[scenario as keyof typeof drawFundsMock] || drawFundsMock['success'];
    return buildMockResult('SaveDrawRecord', scenario, response, getEndpointDelay('SaveDrawRecord', env));
  }

  if (url.includes('DrawFunds') && req.method === 'POST' && isEndpointEnabled('DrawFunds', env)) {
    const scenario = getEndpointScenario('DrawFunds', env, 'success');
    const response = drawFundsMock[scenario as keyof typeof drawFundsMock] || drawFundsMock['success'];
    return buildMockResult('DrawFunds', scenario, response, getEndpointDelay('DrawFunds', env));
  }

  if (url.includes('HelocAutopay') && req.method === 'GET' && isEndpointEnabled('GetHelocAutopayStatus', env)) {
    const scenario = getEndpointScenario('GetHelocAutopayStatus', env, 'disabled');
    const response =
      helocAutopayStatusMock[scenario as keyof typeof helocAutopayStatusMock] || helocAutopayStatusMock['disabled'];
    return buildMockResult('GetHelocAutopayStatus', scenario, response, getEndpointDelay('GetHelocAutopayStatus', env));
  }

  if (url.includes('DocumentList') && req.method === 'POST' && isEndpointEnabled('GetDocuments', env)) {
    const scenario = getEndpointScenario('GetDocuments', env, 'with-documents');
    const response = documentsMock[scenario as keyof typeof documentsMock] || documentsMock['with-documents'];
    return buildMockResult('GetDocuments', scenario, response, getEndpointDelay('GetDocuments', env));
  }

  if (url.includes('DownloadDocument') && isEndpointEnabled('DownloadDocument', env)) {
    const scenario = getEndpointScenario('DownloadDocument', env, 'success');
    const response =
      downloadDocumentMock[scenario as keyof typeof downloadDocumentMock] || downloadDocumentMock['success'];
    return buildMockResult('DownloadDocument', scenario, response, getEndpointDelay('DownloadDocument', env));
  }

  if (
    url.includes('SecureMessageRequest') &&
    url.includes('/Messages') &&
    !url.includes('Delete') &&
    !url.includes('MarkRead') &&
    !url.includes('UnreadCount') &&
    !url.includes('Send') &&
    req.method === 'POST' &&
    isEndpointEnabled('GetSecureMessages', env)
  ) {
    const scenario = getEndpointScenario('GetSecureMessages', env, 'with-messages');
    const response =
      secureMessagesMock[scenario as keyof typeof secureMessagesMock] || secureMessagesMock['with-messages'];
    return buildMockResult('GetSecureMessages', scenario, response, getEndpointDelay('GetSecureMessages', env));
  }

  if (url.includes('UnreadCount') && req.method === 'POST' && isEndpointEnabled('GetUnreadCount', env)) {
    const scenario = getEndpointScenario('GetSecureMessages', env, 'with-messages');
    const messages =
      secureMessagesMock[scenario as keyof typeof secureMessagesMock] || secureMessagesMock['with-messages'];
    return buildMockResult(
      'GetUnreadCount',
      scenario,
      { count: messages.Inbox?.TotalUnreadMessages ?? 0 },
      getEndpointDelay('GetUnreadCount', env),
    );
  }

  if (
    url.includes('Send') &&
    url.includes('Message') &&
    req.method === 'POST' &&
    isEndpointEnabled('SendMessage', env)
  ) {
    const scenario = getEndpointScenario('SendMessage', env, 'success');
    const response = sendMessageMock[scenario as keyof typeof sendMessageMock] || sendMessageMock['success'];
    return buildMockResult('SendMessage', scenario, response, getEndpointDelay('SendMessage', env));
  }

  if (url.includes('MarkRead') && req.method === 'POST' && isEndpointEnabled('MarkMessageRead', env)) {
    const scenario = getEndpointScenario('MarkMessageRead', env, 'success');
    const response =
      markMessageReadMock[scenario as keyof typeof markMessageReadMock] || markMessageReadMock['success'];
    return buildMockResult('MarkMessageRead', scenario, response, getEndpointDelay('MarkMessageRead', env));
  }

  if (url.includes('Delete') && url.includes('Message') && isEndpointEnabled('DeleteMessage', env)) {
    const scenario = getEndpointScenario('DeleteMessage', env, 'success');
    const response = deleteMessageMock[scenario as keyof typeof deleteMessageMock] || deleteMessageMock['success'];
    return buildMockResult('DeleteMessage', scenario, response, getEndpointDelay('DeleteMessage', env));
  }

  if (url.includes('GetCustomerContact') && req.method === 'POST' && isEndpointEnabled('GetContactInfo', env)) {
    const scenario = getEndpointScenario('GetContactInfo', env, 'complete-info');
    const response = contactInfoMock[scenario as keyof typeof contactInfoMock] || contactInfoMock['complete-info'];
    return buildMockResult('GetContactInfo', scenario, response, getEndpointDelay('GetContactInfo', env));
  }

  if (url.includes('ContactInfo') && req.method === 'GET' && isEndpointEnabled('GetContactInfo', env)) {
    const scenario = getEndpointScenario('GetContactInfo', env, 'complete-info');
    const response = contactInfoMock[scenario as keyof typeof contactInfoMock] || contactInfoMock['complete-info'];
    return buildMockResult('GetContactInfo', scenario, response, getEndpointDelay('GetContactInfo', env));
  }

  if (url.includes('UpdateCustomerContact') && req.method === 'POST' && isEndpointEnabled('UpdateContactInfo', env)) {
    const scenario = getEndpointScenario('UpdateContactInfo', env, 'success');
    const response =
      updateContactInfoMock[scenario as keyof typeof updateContactInfoMock] || updateContactInfoMock['success'];
    return buildMockResult('UpdateContactInfo', scenario, response, getEndpointDelay('UpdateContactInfo', env));
  }

  if (
    url.includes('ContactInfo') &&
    !url.includes('CustomerContact') &&
    (req.method === 'POST' || req.method === 'PUT') &&
    isEndpointEnabled('UpdateContactInfo', env)
  ) {
    const scenario = getEndpointScenario('UpdateContactInfo', env, 'success');
    const response =
      updateContactInfoMock[scenario as keyof typeof updateContactInfoMock] || updateContactInfoMock['success'];
    return buildMockResult('UpdateContactInfo', scenario, response, getEndpointDelay('UpdateContactInfo', env));
  }

  if (url.includes('Tcpa') && req.method === 'GET' && isEndpointEnabled('GetTcpaDetails', env)) {
    const scenario = getEndpointScenario('GetTcpaDetails', env, 'needs-consent');
    const response = tcpaDetailsMock[scenario as keyof typeof tcpaDetailsMock] || tcpaDetailsMock['needs-consent'];
    return buildMockResult('GetTcpaDetails', scenario, response, getEndpointDelay('GetTcpaDetails', env));
  }

  if (url.includes('Tcpa') && req.method === 'POST' && isEndpointEnabled('UpdateTcpaConsent', env)) {
    const scenario = getEndpointScenario('UpdateTcpaConsent', env, 'success');
    const response =
      updateTcpaConsentMock[scenario as keyof typeof updateTcpaConsentMock] || updateTcpaConsentMock['success'];
    return buildMockResult('UpdateTcpaConsent', scenario, response, getEndpointDelay('UpdateTcpaConsent', env));
  }

  if (url.includes('Paperless') && req.method === 'POST' && isEndpointEnabled('UpdateLoanPaperlessSettings', env)) {
    const scenario = getEndpointScenario('UpdateLoanPaperlessSettings', env, 'success');
    const response =
      updateLoanPaperlessSettingsMock[scenario as keyof typeof updateLoanPaperlessSettingsMock] ||
      updateLoanPaperlessSettingsMock['success'];
    return buildMockResult(
      'UpdateLoanPaperlessSettings',
      scenario,
      response,
      getEndpointDelay('UpdateLoanPaperlessSettings', env),
    );
  }

  if (url.includes('PayoffQuote') && req.method === 'POST' && isEndpointEnabled('RequestPayoffQuote', env)) {
    const scenario = getEndpointScenario('RequestPayoffQuote', env, 'success');
    const response = payoffQuoteMock[scenario as keyof typeof payoffQuoteMock] || payoffQuoteMock['success'];
    return buildMockResult('RequestPayoffQuote', scenario, response, getEndpointDelay('RequestPayoffQuote', env));
  }

  if (url.includes('configuration/keyvalue') && url.includes('Agm_Api_Key') && req.method === 'GET') {
    const googleMapsEnabled = env.mockConfig?.endpoints?.['GetGoogleMapsApiKey']?.enabled !== false;
    if (googleMapsEnabled) {
      const response = {
        success: false,
        data: null,
      };
      return buildMockResult('GetGoogleMapsApiKey', 'disabled', response, 0);
    }
  }

  if (url.includes('/user/status') && req.method === 'GET') {
    const userStatusEnabled = env.mockConfig?.endpoints?.['GetMlaUserStatus']?.enabled !== false;
    if (userStatusEnabled) {
      const response = {
        success: true,
        data: {
          status: 'Exist',
          epsUser: { userId: 'mock-eps-user-id', email: 'test@test.com' },
          firstName: 'Mock',
          lastName: 'User',
        },
      };
      return buildMockResult('GetMlaUserStatus', 'exists', response, 0);
    }
  }

  if (url.includes('/verify/user/send-verification') && req.method === 'POST') {
    const sendVerifyEnabled = env.mockConfig?.endpoints?.['MlaSendVerification']?.enabled !== false;
    if (sendVerifyEnabled) {
      return buildMockResult('MlaSendVerification', 'success', { success: true }, 0);
    }
  }

  if (url.includes('/verify/user/check-verification') && req.method === 'POST') {
    const checkVerifyEnabled = env.mockConfig?.endpoints?.['MlaCheckVerification']?.enabled !== false;
    if (checkVerifyEnabled) {
      return buildMockResult('MlaCheckVerification', 'success', { success: true }, 0);
    }
  }

  if (url.includes('borrower-info') && req.method === 'GET' && isEndpointEnabled('GetMlaBorrowerInfo', env)) {
    const scenario = getEndpointScenario('GetMlaBorrowerInfo', env, 'default');
    const response =
      mlaBorrowerInfoMock[scenario as keyof typeof mlaBorrowerInfoMock] ?? mlaBorrowerInfoMock['default'];
    return buildMockResult('GetMlaBorrowerInfo', scenario, response, getEndpointDelay('GetMlaBorrowerInfo', env));
  }

  if (url.includes('/session/sso') && req.method === 'GET') {
    const mlaSsoEnabled = env.mockConfig?.endpoints?.['MlaSsoToken']?.enabled !== false;
    if (mlaSsoEnabled) {
      const ssoUrl = new URL(url, 'http://localhost');
      const ssoLoanId = ssoUrl.searchParams.get('loanId') || 'refi-001';
      const response = {
        success: true,
        data: 'mock-mla-sso-token-for-cp-exchange-' + ssoLoanId + '-' + Date.now(),
      };
      return buildMockResult('MlaSsoToken', 'success', response, 0);
    }
  }

  if (url.includes('/auth/sso') && req.method === 'POST') {
    const cpSsoEnabled = env.mockConfig?.endpoints?.['CpSsoLogin']?.enabled !== false;
    if (cpSsoEnabled) {
      const ssoBody = req.body as { ssoToken?: string } | null;
      const ssoTokenValue = ssoBody?.ssoToken || '';
      const tokenLoanIdMatch = ssoTokenValue.match(/mock-mla-sso-token-for-cp-exchange-(.+?)-\d+$/);
      const cpLoanId = tokenLoanIdMatch ? tokenLoanIdMatch[1] : 'refi-001';
      const response = {
        userGuid: 'mock-user-guid-12345',
        authToken: 'mock-cp-auth-token-' + Date.now(),
        expiresInSeconds: 3600,
        borrowerId: 'borrower-001',
        loanId: cpLoanId,
        email: 'john.doe@example.com',
        visitorId: 'mock-visitor-id-67890',
        mlaDomainLink: 'https://mla.loandepot.com',
      };
      return buildMockResult('CpSsoLogin', 'success', response, 0);
    }
  }

  if (url.includes('/auth/ula') && req.method === 'POST') {
    const cpUlaEnabled = env.mockConfig?.endpoints?.['CpUlaToken']?.enabled !== false;
    if (cpUlaEnabled) {
      const response = {
        ssoToken: 'mock-ula-sso-token-' + Date.now(),
      };
      return buildMockResult('CpUlaToken', 'success', response, 0);
    }
  }

  if (url.includes('/auth/user') && !url.includes('/auth/user/') && req.method === 'GET' && isEndpointEnabled('GetAuthUser', env)) {
    const scenario = getEndpointScenario('GetAuthUser', env, 'multiple-borrowers');
    const response = mockAuthUserProfile[scenario as keyof typeof mockAuthUserProfile] || mockAuthUserProfile['multiple-borrowers'];
    return buildMockResult('GetAuthUser', scenario, response, getEndpointDelay('GetAuthUser', env));
  }

  if (url.includes('/auth/switch') && req.method === 'POST') {
    const switchBorrowerEnabled = env.mockConfig?.endpoints?.['SwitchBorrower']?.enabled !== false;
    if (switchBorrowerEnabled) {
      const body = req.body as { id?: string; verify?: string } | null;
      const borrowerId = body?.id || 'borrower-002';
      const ssnLastFour = body?.verify || '';

      const isValidSsn = ssnLastFour === '1234';

      if (!isValidSsn) {
        return buildMockResult(
          'SwitchBorrower',
          'invalid-ssn',
          { error: 'Invalid SSN', message: 'The SSN digits you entered do not match our records.' },
          getEndpointDelay('SwitchBorrower', env),
          401,
        );
      }

      const response = {
        userGuid: 'mock-user-guid-12345',
        authToken: 'mock-cp-auth-token-switched-' + Date.now(),
        expiresInSeconds: 3600,
        borrowerId: borrowerId,
        loanId: 'refi-001',
        email: borrowerId === 'borrower-002' ? 'jane.doe@example.com' : 'john.doe@example.com',
        visitorId: 'mock-visitor-id-67890',
        mlaDomainLink: 'https://mla.loandepot.com',
      };
      return buildMockResult('SwitchBorrower', 'success', response, getEndpointDelay('SwitchBorrower', env));
    }
  }

  if (
    url.includes('/dashboard') &&
    (url.includes('borrowerToken') || req.params.has('borrowerToken')) &&
    req.method === 'GET' &&
    isEndpointEnabled('GetMlaDashboard', env)
  ) {
    const scenario = getEndpointScenario('GetMlaDashboard', env, 'no-application');
    const response = mlaDashboardMock[scenario as keyof typeof mlaDashboardMock] ?? mlaDashboardMock['no-application'];
    return buildMockResult('GetMlaDashboard', scenario, response, getEndpointDelay('GetMlaDashboard', env));
  }

  if (
    url.includes('OriginationApplications') &&
    req.method === 'GET' &&
    isEndpointEnabled('GetOriginationApplications', env)
  ) {
    const scenario = getEndpointScenario('GetOriginationApplications', env, 'no-application');
    const response =
      originationApplicationsMock[scenario as keyof typeof originationApplicationsMock] ??
      originationApplicationsMock['no-application'];
    return buildMockResult(
      'GetOriginationApplications',
      scenario,
      response,
      getEndpointDelay('GetOriginationApplications', env),
    );
  }

  const loanDetailsMatch = url.match(/\/api\/v\d+\/loans\/([^/]+)$/);
  if (loanDetailsMatch && req.method === 'GET' && isEndpointEnabled('GetOriginationLoanDetails', env)) {
    const scenario = getEndpointScenario('GetOriginationLoanDetails', env, 'default');
    const response =
      loanDetailScenarios[scenario as keyof typeof loanDetailScenarios] ?? loanDetailScenarios['default'];
    return buildMockResult(
      'GetOriginationLoanDetails',
      scenario,
      response,
      getEndpointDelay('GetOriginationLoanDetails', env),
    );
  }

  const documentHistoryMatch = url.match(/\/api\/v\d+\/loans\/([^/]+)\/documents$/);
  if (documentHistoryMatch && req.method === 'GET' && isEndpointEnabled('GetDocumentHistory', env)) {
    const scenario = getEndpointScenario('GetDocumentHistory', env, 'default');
    const response =
      originationDocumentListScenarios[scenario as keyof typeof originationDocumentListScenarios] ??
      originationDocumentListScenarios['default'];
    const wrappedResponse = { documents: response };
    return buildMockResult(
      'GetDocumentHistory',
      scenario,
      wrappedResponse,
      getEndpointDelay('GetDocumentHistory', env),
    );
  }

  if (
    url.includes('/origination/') &&
    url.includes('/progress') &&
    req.method === 'GET' &&
    isEndpointEnabled('GetLoanProgress', env)
  ) {
    const scenario = getEndpointScenario('GetLoanProgress', env, 'default');
    const response =
      loanProgressScenarios[scenario as keyof typeof loanProgressScenarios] ?? loanProgressScenarios['default'];
    return buildMockResult('GetLoanProgress', scenario, response, getEndpointDelay('GetLoanProgress', env));
  }

  if (
    url.includes('/origination/') &&
    url.includes('/team') &&
    req.method === 'GET' &&
    isEndpointEnabled('GetLoanTeam', env)
  ) {
    const teamLoanIdMatch = url.match(/\/api\/v\d+\/loans\/([^/]+)\/team/);
    const teamLoanId = teamLoanIdMatch ? teamLoanIdMatch[1] : '';

    let scenario: string;
    if (teamLoanId.endsWith('refi-001')) {
      scenario = 'full-team';
    } else {
      scenario = getEndpointScenario('GetLoanTeam', env, 'default');
    }

    const response = loanTeamScenarios[scenario as keyof typeof loanTeamScenarios] ?? loanTeamScenarios['default'];
    return buildMockResult('GetLoanTeam', scenario, response, getEndpointDelay('GetLoanTeam', env));
  }

  if (
    req.method === 'GET' &&
    /\/api\/v\d+\/loans\/[^/]+\/contacts(?:\?|$)/.test(url) &&
    isEndpointEnabled('LoadOriginationContacts', env)
  ) {
    const contactsLoanIdMatch = url.match(/\/api\/v\d+\/loans\/([^/]+)\/contacts/);
    const contactsLoanId = contactsLoanIdMatch ? contactsLoanIdMatch[1] : '';

    let scenario: string;
    if (contactsLoanId.endsWith('refi-001')) {
      scenario = 'full-team';
    } else {
      scenario = getEndpointScenario('LoadOriginationContacts', env, 'default');
    }

    const response =
      originationContactsScenarios[scenario as keyof typeof originationContactsScenarios] ??
      originationContactsScenarios['default'];
    return buildMockResult(
      'LoadOriginationContacts',
      scenario,
      response,
      getEndpointDelay('LoadOriginationContacts', env),
    );
  }

  if (
    req.method === 'GET' &&
    /\/api\/v\d+\/loans\/[^/]+\/messages(?:\?|$)/.test(url) &&
    isEndpointEnabled('LoadOriginationMessages', env)
  ) {
    const scenario = getEndpointScenario('LoadOriginationMessages', env, 'default');
    const response = scenario === 'stateful'
      ? originationMessageMockState.getMessages()
      : originationMessagesScenarios[scenario as keyof typeof originationMessagesScenarios] ??
        originationMessagesScenarios['default'];
    return buildMockResult(
      'LoadOriginationMessages',
      scenario,
      response,
      getEndpointDelay('LoadOriginationMessages', env),
    );
  }

  if (
    req.method === 'GET' &&
    /\/api\/v\d+\/loans\/[^/]+\/requests(?:\?|$)/.test(url) &&
    !url.includes('/requests/summary') &&
    isEndpointEnabled('GetOriginationTodos', env)
  ) {
    const scenario = getEndpointScenario('GetOriginationTodos', env, 'default');
    const response =
      originationTodosScenarios[scenario as keyof typeof originationTodosScenarios] ??
      originationTodosScenarios['default'];
    return buildMockResult('GetOriginationTodos', scenario, response, getEndpointDelay('GetOriginationTodos', env));
  }

  if (url.match(/\/api\/v\d+\/loan$/) && req.method === 'POST' && isEndpointEnabled('CreateLoan', env)) {
    const scenario = getEndpointScenario('CreateLoan', env, 'default');
    const response = createLoanScenarios[scenario] ?? createLoanScenarios['default'];
    return buildMockResult('CreateLoan', scenario, response, getEndpointDelay('CreateLoan', env));
  }

  if (url.match(/\/api\/v\d+\/loan\/[^/]+$/) && req.method === 'PATCH' && isEndpointEnabled('UpdateLoan', env)) {
    const scenario = getEndpointScenario('UpdateLoan', env, 'default');
    const response = updateLoanScenarios[scenario] ?? updateLoanScenarios['default'];
    return buildMockResult('UpdateLoan', scenario, response, getEndpointDelay('UpdateLoan', env));
  }

  if (url.match(/\/api\/v\d+\/borrower$/) && req.method === 'POST' && isEndpointEnabled('CreateBorrower', env)) {
    const scenario = getEndpointScenario('CreateBorrower', env, 'default');
    const response = createBorrowerScenarios[scenario] ?? createBorrowerScenarios['default'];
    return buildMockResult('CreateBorrower', scenario, response, getEndpointDelay('CreateBorrower', env));
  }

  if (
    url.match(/\/api\/v\d+\/borrower\/[^/]+$/) &&
    req.method === 'PATCH' &&
    isEndpointEnabled('UpdateBorrower', env)
  ) {
    const scenario = getEndpointScenario('UpdateBorrower', env, 'default');
    const response = updateBorrowerScenarios[scenario] ?? updateBorrowerScenarios['default'];
    return buildMockResult('UpdateBorrower', scenario, response, getEndpointDelay('UpdateBorrower', env));
  }

  if (url.match(/\/api\/v\d+\/income$/) && req.method === 'POST' && isEndpointEnabled('CreateIncome', env)) {
    const scenario = getEndpointScenario('CreateIncome', env, 'default');
    const response = createIncomeScenarios[scenario] ?? createIncomeScenarios['default'];
    return buildMockResult('CreateIncome', scenario, response, getEndpointDelay('CreateIncome', env));
  }

  if (url.match(/\/api\/v\d+\/income\/[^/]+$/) && req.method === 'PATCH' && isEndpointEnabled('UpdateIncome', env)) {
    const scenario = getEndpointScenario('UpdateIncome', env, 'default');
    const response = updateIncomeScenarios[scenario] ?? updateIncomeScenarios['default'];
    return buildMockResult('UpdateIncome', scenario, response, getEndpointDelay('UpdateIncome', env));
  }

  if (url.match(/\/api\/v\d+\/income\/[^/]+$/) && req.method === 'DELETE' && isEndpointEnabled('DeleteIncome', env)) {
    const scenario = getEndpointScenario('DeleteIncome', env, 'default');
    const response = deleteIncomeScenarios[scenario] ?? deleteIncomeScenarios['default'];
    return buildMockResult('DeleteIncome', scenario, response, getEndpointDelay('DeleteIncome', env));
  }

  if (url.match(/\/api\/v\d+\/asset$/) && req.method === 'POST' && isEndpointEnabled('CreateAsset', env)) {
    const scenario = getEndpointScenario('CreateAsset', env, 'default');
    const response = createAssetScenarios[scenario] ?? createAssetScenarios['default'];
    return buildMockResult('CreateAsset', scenario, response, getEndpointDelay('CreateAsset', env));
  }

  if (url.match(/\/api\/v\d+\/asset\/[^/]+$/) && req.method === 'PATCH' && isEndpointEnabled('UpdateAsset', env)) {
    const scenario = getEndpointScenario('UpdateAsset', env, 'default');
    const response = updateAssetScenarios[scenario] ?? updateAssetScenarios['default'];
    return buildMockResult('UpdateAsset', scenario, response, getEndpointDelay('UpdateAsset', env));
  }

  if (url.match(/\/api\/v\d+\/asset\/[^/]+$/) && req.method === 'DELETE' && isEndpointEnabled('DeleteAsset', env)) {
    const scenario = getEndpointScenario('DeleteAsset', env, 'default');
    const response = deleteAssetScenarios[scenario] ?? deleteAssetScenarios['default'];
    return buildMockResult('DeleteAsset', scenario, response, getEndpointDelay('DeleteAsset', env));
  }

  if (url.includes('LoanMessages') && req.method === 'POST' && isEndpointEnabled('GetLoanMessages', env)) {
    const scenario = getEndpointScenario('GetLoanMessages', env, 'with-messages');
    const loanRequests = (req.body as { loanNumber: string }[]) || [];
    const loanNumber = loanRequests[0]?.loanNumber || '1234567890';

    const messagesResponse =
      scenario === 'no-messages'
        ? { messages: [] }
        : {
            messages: [
              {
                id: 0,
                loanNumber,
                messageType: 0,
                messageContent: {
                  body: 'IRS Form 1098 (Mortgage Interest Statement) may be included with your billing statement in January, and mailed no later than January 31, 2026. They are currently available <a href="#STATEMENTS_AND_DOCUMENTS">here</a> and in the loanDepot mobile app.',
                  messageType: 'info2',
                  bgColor: '#CFE1F1',
                  textColor: '#000000',
                },
                startTime: '2026-01-12T00:00:00',
                endTime: '2026-03-31T23:59:00',
              },
              {
                id: 0,
                loanNumber,
                messageType: 0,
                messageContent: {
                  body: 'Your recent payment is in process and cannot be edited using the online system at this time. If you wish to make changes to this payment, please contact Customer Service at <a href="tel:(866) 258-6572">(866) 258-6572</a> for assistance.',
                  messageType: 'alert1',
                  bgColor: '#FFEECC',
                  textColor: '#000000',
                },
                startTime: '2024-08-06T00:00:00',
                endTime: '2026-09-04T23:59:00',
              },
              {
                id: 0,
                loanNumber,
                messageType: 0,
                messageContent: {
                  body: 'Increasing postal delays have been challenging for many of our customers recently, and when a weather event hits, it can further decrease reliable on-time mail delivery. To avoid the risk of a late payment, we urge you to use our free, online payment portal to help ensure on-time delivery.',
                  messageType: 'alert1',
                  bgColor: '#FFEECC',
                  textColor: '#000000',
                },
                startTime: '2024-03-01T21:54:20.7466667',
                endTime: '2040-05-15T17:00:00',
              },
            ],
          };

    return buildMockResult('GetLoanMessages', scenario, messagesResponse, getEndpointDelay('GetLoanMessages', env));
  }

  if (url.includes('GetLoan/') && req.method === 'GET' && isEndpointEnabled('GetLoan', env)) {
    const loanNumberMatch = url.match(/GetLoan\/(\d+)\//);
    const requestedLoanNumber = loanNumberMatch ? loanNumberMatch[1] : null;
    const scenario = getEndpointScenario('GetLoans', env, 'multiple-loans');
    const allLoans =
      loanResponsesMock[scenario as keyof typeof loanResponsesMock] || loanResponsesMock['multiple-loans'];
    const matchedLoan = requestedLoanNumber
      ? (allLoans as { loan: { loanNumber: string } }[]).find(
          (lr: { loan: { loanNumber: string } }) => lr.loan.loanNumber === requestedLoanNumber,
        )
      : (allLoans as unknown[])[0];
    return buildMockResult('GetLoan', 'default', matchedLoan ?? (allLoans as unknown[])[0], getEndpointDelay('GetLoan', env));
  }

  if (url.includes('OtherLoans/') && req.method === 'GET' && isEndpointEnabled('GetOtherLoans', env)) {
    return buildMockResult('GetOtherLoans', 'default', [], getEndpointDelay('GetOtherLoans', env));
  }

  if (url.includes('GetQrpcTemplate') && req.method === 'GET' && isEndpointEnabled('GetQrpcTemplate', env)) {
    return buildMockResult(
      'GetQrpcTemplate',
      'default',
      { template: null, isAvailable: false },
      getEndpointDelay('GetQrpcTemplate', env),
    );
  }

  if (
    url.includes('lossmitigationworkstationdata') &&
    req.method === 'GET' &&
    isEndpointEnabled('GetLossMitigationData', env)
  ) {
    return buildMockResult(
      'GetLossMitigationData',
      'default',
      { hasActiveCases: false, cases: [] },
      getEndpointDelay('GetLossMitigationData', env),
    );
  }

  if (url.match(/\/Payments\/\d+\/[^/]+$/) && req.method === 'PUT' && isEndpointEnabled('UpdateAutopay', env)) {
    const scenario = getEndpointScenario('UpdateAutopay', env, 'success');
    const response =
      autopaySetupMockResponses[scenario as keyof typeof autopaySetupMockResponses] ||
      autopaySetupMockResponses['success'];
    const status = response && 'isSuccess' in response && !response.isSuccess ? 400 : 200;
    return buildMockResult('UpdateAutopay', scenario, response, getEndpointDelay('UpdateAutopay', env), status);
  }

  if (url.includes('CreditMonitoring') && req.method === 'GET' && isEndpointEnabled('GetCreditWidgetUrl', env)) {
    return buildMockResult(
      'GetCreditWidgetUrl',
      'default',
      { url: '', isAvailable: false },
      getEndpointDelay('GetCreditWidgetUrl', env),
    );
  }

  if (
    url.match(/\/Payments\/\d+\/[^/]+$/) &&
    req.method === 'PUT' &&
    !isEndpointEnabled('UpdateAutopay', env) &&
    isEndpointEnabled('UpdatePayment', env)
  ) {
    return buildMockResult(
      'UpdatePayment',
      'success',
      { confirmationNumber: 'MOCK-CONF-' + Date.now(), isSuccess: true },
      getEndpointDelay('UpdatePayment', env),
    );
  }

  if (
    url.match(/\/Payments\/\d+\/Pending\/[^/]+$/) &&
    req.method === 'GET' &&
    isEndpointEnabled('GetPendingPaymentById', env)
  ) {
    const paymentIdMatch = url.match(/\/Pending\/([^/?]+)/);
    const paymentId = paymentIdMatch?.[1] ?? 'otp-1';
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 5);
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 15);

    return buildMockResult(
      'GetPendingPaymentById',
      'default',
      {
        paymentId,
        loanNumber: 1234567890,
        confirmationNumber: `CONF-OTP-${paymentId}`,
        createdAt: new Date().toISOString(),
        paymentDate: futureDate.toISOString(),
        paymentStatus: 3,
        totalDraftAmount: 2847.32,
        monthlyPaymentAmount: 2847.32,
        paymentDue: [
          {
            dueDate: dueDate.toISOString(),
            totalMonthlyPayment: 2847.32,
            principalAndInterest: 2135.49,
            taxAndInsurance: 711.83,
            isSuspense: false,
          },
        ],
        fees: [
          { type: 1, code: '1', description: 'Late Fee', amount: 30.0, breakdown: null },
          { type: 2, code: '2', description: 'NSF Fee', amount: 25.0, breakdown: null },
        ],
        additionalPrincipal: 0,
        additionalEscrow: 0,
        paymentProfileId: 'mock-chase-1',
        paymentProfileDetails: {
          firstName: 'John',
          lastName: 'Smith',
          accountType: 0,
          routingNumber: '021000021',
          accountNumber: '****4567',
          bankName: 'Chase Bank',
        },
        paymentFrequency: 0,
        channelId: 2,
        paymentCancellationCutoffDate: futureDate.toISOString(),
        delinquency: {
          contactCode: '043',
          responseCode: 'ppr',
          reasonCode: '002',
        },
      },
      getEndpointDelay('GetPendingPaymentById', env),
    );
  }

  if (
    url.match(/\/PaymentProfile\/[^/]+\/[^/]+$/) &&
    req.method === 'DELETE' &&
    isEndpointEnabled('DeletePaymentProfile', env)
  ) {
    return buildMockResult(
      'DeletePaymentProfile',
      'success',
      { success: true },
      getEndpointDelay('DeletePaymentProfile', env),
    );
  }

  if (
    url.match(/\/PaymentProfile\/[^/]+$/) &&
    req.method === 'PUT' &&
    isEndpointEnabled('SetDefaultPaymentProfile', env)
  ) {
    return buildMockResult(
      'SetDefaultPaymentProfile',
      'success',
      { success: true },
      getEndpointDelay('SetDefaultPaymentProfile', env),
    );
  }

  if (url.includes('payment-lock') && req.method === 'GET' && isEndpointEnabled('GetPaymentLockStatus', env)) {
    return buildMockResult(
      'GetPaymentLockStatus',
      'default',
      { isLocked: false, lockExpirationDate: null },
      getEndpointDelay('GetPaymentLockStatus', env),
    );
  }

  if (url.includes('urlas') && req.method === 'GET' && isEndpointEnabled('GetURLAs', env)) {
    return buildMockResult('GetURLAs', 'default', { url: '' }, getEndpointDelay('GetURLAs', env));
  }

  if (url.includes('credit-card-config') && req.method === 'GET' && isEndpointEnabled('GetCreditCardConfig', env)) {
    return buildMockResult(
      'GetCreditCardConfig',
      'default',
      { isEnabled: false, config: null },
      getEndpointDelay('GetCreditCardConfig', env),
    );
  }

  if (
    url.match(/\/loans\/[^/]+\/requests(?:\?|$)/) &&
    !url.includes('/requests/summary') &&
    req.method === 'GET' &&
    isEndpointEnabled('LoadDocumentRequests', env)
  ) {
    return buildMockResult(
      'LoadDocumentRequests',
      'default',
      { requests: [] },
      getEndpointDelay('LoadDocumentRequests', env),
    );
  }

  if (url.includes('/documents') && req.method === 'POST' && isEndpointEnabled('UploadDocument', env)) {
    return buildMockResult(
      'UploadDocument',
      'success',
      { documentId: 'mock-doc-' + Date.now(), success: true },
      getEndpointDelay('UploadDocument', env),
    );
  }

  if (
    url.includes('/documents/') &&
    url.includes('/embed') &&
    req.method === 'GET' &&
    isEndpointEnabled('GetDocumentEmbed', env)
  ) {
    return buildMockResult(
      'GetDocumentEmbed',
      'default',
      { embedUrl: 'https://www.loandepot.com/mortgageprocessbrochure', isAvailable: true },
      getEndpointDelay('GetDocumentEmbed', env),
    );
  }

  if (
    url.includes('/documents/') &&
    url.includes('/confirm') &&
    req.method === 'POST' &&
    isEndpointEnabled('ConfirmDocument', env)
  ) {
    return buildMockResult('ConfirmDocument', 'success', { success: true }, getEndpointDelay('ConfirmDocument', env));
  }

  if (
    url.includes('/documents/') &&
    url.includes('/metadata') &&
    req.method === 'GET' &&
    isEndpointEnabled('GetDocumentMetadata', env)
  ) {
    return buildMockResult(
      'GetDocumentMetadata',
      'default',
      { metadata: null },
      getEndpointDelay('GetDocumentMetadata', env),
    );
  }

  if (url.includes('data-first') && req.method === 'POST' && isEndpointEnabled('CreateDataFirstSession', env)) {
    return buildMockResult(
      'CreateDataFirstSession',
      'default',
      { sessionId: 'mock-session-' + Date.now(), redirectUrl: '' },
      getEndpointDelay('CreateDataFirstSession', env),
    );
  }

  if (
    url.includes('asset-verification') &&
    req.method === 'POST' &&
    isEndpointEnabled('CompleteAssetVerification', env)
  ) {
    return buildMockResult(
      'CompleteAssetVerification',
      'success',
      { success: true },
      getEndpointDelay('CompleteAssetVerification', env),
    );
  }

  if (
    url.includes('income-employment') &&
    req.method === 'POST' &&
    isEndpointEnabled('CreateIncomeEmploymentSession', env)
  ) {
    return buildMockResult(
      'CreateIncomeEmploymentSession',
      'default',
      { sessionId: 'mock-session-' + Date.now(), redirectUrl: '' },
      getEndpointDelay('CreateIncomeEmploymentSession', env),
    );
  }

  if (
    url.includes('income-verification') &&
    req.method === 'POST' &&
    isEndpointEnabled('CompleteIncomeVerification', env)
  ) {
    return buildMockResult(
      'CompleteIncomeVerification',
      'success',
      { success: true },
      getEndpointDelay('CompleteIncomeVerification', env),
    );
  }

  if (
    url.includes('employment-verification') &&
    req.method === 'POST' &&
    isEndpointEnabled('CompleteEmploymentVerification', env)
  ) {
    return buildMockResult(
      'CompleteEmploymentVerification',
      'success',
      { success: true },
      getEndpointDelay('CompleteEmploymentVerification', env),
    );
  }

  if (
    url.includes('secondary-income') &&
    req.method === 'POST' &&
    isEndpointEnabled('CompleteSecondaryIncomeVerification', env)
  ) {
    return buildMockResult(
      'CompleteSecondaryIncomeVerification',
      'success',
      { success: true },
      getEndpointDelay('CompleteSecondaryIncomeVerification', env),
    );
  }

  if (url.includes('/actions') && req.method === 'GET' && isEndpointEnabled('LoadBorrowerActions', env)) {
    return buildMockResult(
      'LoadBorrowerActions',
      'default',
      {
        actions: [
          {
            actionId: 'insurance-action-1',
            borrowerId: 'mock-borrower-id',
            name: 'Homeowners Insurance Quotes',
            description: 'Get a homeowners insurance quote',
            requestedOn: new Date().toISOString(),
            requestAction: 'ViewQuote',
            actionOrder: 1,
            vendors: ['Hippo', 'Geico'],
            tooltip: {
              iconType: 'info',
              text: 'Get a quote for homeowners insurance',
              buttonAltText: 'View Quote',
            },
          },
        ],
      },
      getEndpointDelay('LoadBorrowerActions', env),
    );
  }

  if (url.match(/\/actions\/[^/]+$/) && req.method === 'GET' && isEndpointEnabled('GetBorrowerAction', env)) {
    return buildMockResult(
      'GetBorrowerAction',
      'default',
      { action: null },
      getEndpointDelay('GetBorrowerAction', env),
    );
  }

  if (url.match(/\/actions\/[^/]+\/complete$/) && req.method === 'POST' && isEndpointEnabled('CompleteAction', env)) {
    return buildMockResult('CompleteAction', 'success', { success: true }, getEndpointDelay('CompleteAction', env));
  }

  if (
    url.includes('/origination/') &&
    url.includes('/payment') &&
    req.method === 'POST' &&
    isEndpointEnabled('SubmitOriginationPayment', env)
  ) {
    return buildMockResult(
      'SubmitOriginationPayment',
      'success',
      { confirmationNumber: 'MOCK-ORIG-' + Date.now(), success: true },
      getEndpointDelay('SubmitOriginationPayment', env),
    );
  }

  if (
    url.includes('/insurance/eligibility') &&
    req.method === 'GET' &&
    isEndpointEnabled('GetInsuranceEligibility', env)
  ) {
    const scenario = getEndpointScenario('GetInsuranceEligibility', env, 'default');
    const eligibilityResponses: Record<string, unknown> = {
      default: {
        isEligible: true,
        vendors: ['geico', 'mello'],
        ineligibilityReason: null,
      },
      ineligible: {
        isEligible: false,
        vendors: [],
        ineligibilityReason: 'Borrower does not meet eligibility requirements',
      },
      'geico-only': {
        isEligible: true,
        vendors: ['geico'],
        ineligibilityReason: null,
      },
      'mello-only': {
        isEligible: true,
        vendors: ['mello'],
        ineligibilityReason: null,
      },
    };
    return buildMockResult(
      'GetInsuranceEligibility',
      scenario,
      eligibilityResponses[scenario] || eligibilityResponses['default'],
      getEndpointDelay('GetInsuranceEligibility', env),
    );
  }

  if (url.includes('/insurance') && req.method === 'GET' && isEndpointEnabled('LoadInsurance', env)) {
    return buildMockResult(
      'LoadInsurance',
      'default',
      { insurance: null, isRequired: false },
      getEndpointDelay('LoadInsurance', env),
    );
  }

  if (url.includes('/insurance') && req.method === 'POST' && isEndpointEnabled('SubmitInsurance', env)) {
    return buildMockResult('SubmitInsurance', 'success', { success: true }, getEndpointDelay('SubmitInsurance', env));
  }

  if (url.includes('/insurance') && req.method === 'PUT' && isEndpointEnabled('UpdateInsurance', env)) {
    return buildMockResult('UpdateInsurance', 'success', { success: true }, getEndpointDelay('UpdateInsurance', env));
  }

  if (url.includes('/gift-letters') && req.method === 'GET' && isEndpointEnabled('LoadGiftLetters', env)) {
    return buildMockResult('LoadGiftLetters', 'default', { giftLetters: [] }, getEndpointDelay('LoadGiftLetters', env));
  }

  if (url.includes('/gift-letters') && req.method === 'POST' && isEndpointEnabled('SubmitGiftLetter', env)) {
    return buildMockResult(
      'SubmitGiftLetter',
      'success',
      { giftLetterId: 'mock-gift-' + Date.now(), success: true },
      getEndpointDelay('SubmitGiftLetter', env),
    );
  }

  if (url.includes('/appraisal') && req.method === 'GET' && isEndpointEnabled('LoadAppraisal', env)) {
    return buildMockResult(
      'LoadAppraisal',
      'default',
      { appraisal: null, status: 'NotStarted' },
      getEndpointDelay('LoadAppraisal', env),
    );
  }

  if (
    url.includes('/econsent') &&
    !url.includes('/tcpaconsent') &&
    req.method === 'GET' &&
    isEndpointEnabled('GetEConsent', env)
  ) {
    const scenario = getEndpointScenario('GetEConsent', env, 'needs-consent');
    const needsConsent = scenario === 'needs-consent';
    return buildMockResult(
      'GetEConsent',
      scenario,
      { consentGiven: !needsConsent, consentDate: needsConsent ? null : new Date().toISOString() },
      getEndpointDelay('GetEConsent', env),
    );
  }

  if (
    url.includes('/econsent') &&
    !url.includes('/tcpaconsent') &&
    req.method === 'PUT' &&
    isEndpointEnabled('UpdateEConsent', env)
  ) {
    return buildMockResult(
      'UpdateEConsent',
      'success',
      { consentGiven: true, consentDate: new Date().toISOString() },
      getEndpointDelay('UpdateEConsent', env),
    );
  }

  if (url.includes('/tcpaconsent') && req.method === 'GET' && isEndpointEnabled('GetOriginationTcpaConsent', env)) {
    const scenario = getEndpointScenario('GetOriginationTcpaConsent', env, 'needs-consent');
    const needsConsent = scenario === 'needs-consent';
    return buildMockResult(
      'GetOriginationTcpaConsent',
      scenario,
      { isLoanConsented: !needsConsent, isBorrowerConsented: !needsConsent },
      getEndpointDelay('GetOriginationTcpaConsent', env),
    );
  }

  if (url.includes('/tcpaconsent') && req.method === 'POST' && isEndpointEnabled('UpdateOriginationTcpaConsent', env)) {
    return buildMockResult(
      'UpdateOriginationTcpaConsent',
      'success',
      { isLoanConsented: true, isBorrowerConsented: true },
      getEndpointDelay('UpdateOriginationTcpaConsent', env),
    );
  }

  if (
    url.includes('instant-verification') &&
    req.method === 'POST' &&
    isEndpointEnabled('UpdateInstantVerificationConsent', env)
  ) {
    return buildMockResult(
      'UpdateInstantVerificationConsent',
      'success',
      { success: true },
      getEndpointDelay('UpdateInstantVerificationConsent', env),
    );
  }

  if (
    url.match(/\/loans\/[^/]+\/messages$/) &&
    req.method === 'POST' &&
    isEndpointEnabled('SendOriginationMessage', env)
  ) {
    return buildMockResult(
      'SendOriginationMessage',
      'success',
      { messageId: 'mock-msg-' + Date.now(), success: true },
      getEndpointDelay('SendOriginationMessage', env),
    );
  }

  if (
    url.includes('/messages/') &&
    url.includes('/read') &&
    req.method === 'POST' &&
    isEndpointEnabled('MarkOriginationMessagesRead', env)
  ) {
    const body = req.body as { messageIds?: string[] } | null;
    if (body?.messageIds) {
      originationMessageMockState.markAsRead(body.messageIds);
    }
    return buildMockResult(
      'MarkOriginationMessagesRead',
      'success',
      { success: true },
      getEndpointDelay('MarkOriginationMessagesRead', env),
    );
  }

  if (
    req.method === 'GET' &&
    /\/api\/v\d+\/loans\/[^/]+\/borrowers\/[^/]+\/metadata(?:\?|$)/.test(url) &&
    isEndpointEnabled('LoadBorrowerMetadata', env)
  ) {
    const scenario = getEndpointScenario('LoadBorrowerMetadata', env, 'default');
    const response = scenario === 'stateful'
      ? { ...originationMetadataScenarios['default'], unreadMessageCount: originationMessageMockState.getUnreadCount() }
      : originationMetadataScenarios[scenario as keyof typeof originationMetadataScenarios] ??
        originationMetadataScenarios['default'];
    return buildMockResult(
      'LoadBorrowerMetadata',
      scenario,
      response,
      getEndpointDelay('LoadBorrowerMetadata', env),
    );
  }

  if (url.includes('/callback') && req.method === 'POST' && isEndpointEnabled('RequestCallback', env)) {
    return buildMockResult(
      'RequestCallback',
      'success',
      { requestId: 'mock-callback-' + Date.now(), success: true },
      getEndpointDelay('RequestCallback', env),
    );
  }

  if (
    (url.includes('/register/check-token') || (url.includes('/register/check') && !url.includes('/check-token'))) &&
    req.method === 'GET' &&
    isEndpointEnabled('CheckRegistrationToken', env)
  ) {
    return buildMockResult(
      'CheckRegistrationToken',
      'default',
      { customerGuid: 'mock-guid-123', loanId: 'mock-loan-456', username: 'user@example.com', isActive: false },
      getEndpointDelay('CheckRegistrationToken', env),
    );
  }

  if (url.includes('/register/confirm-login') && req.method === 'POST' && isEndpointEnabled('ConfirmRegistration', env)) {
    return buildMockResult(
      'ConfirmRegistration',
      'success',
      { userGuid: 'mock-user-guid-123', email: 'user@example.com', loanId: 'mock-loan-456', borrowerId: 'mock-borrower-789', activationToken: 'mock-activation-token-abc', firstName: 'Mock', lastName: 'User' },
      getEndpointDelay('ConfirmRegistration', env),
    );
  }

  if (url.includes('/api/v1/user/azure') && req.method === 'POST' && isEndpointEnabled('MlaCreateAccountWithUserGuid', env)) {
    return buildMockResult(
      'MlaCreateAccountWithUserGuid',
      'success',
      { success: true, data: { userId: 'mock-mla-user-001', token: 'mock-eps-token-xyz' } },
      getEndpointDelay('MlaCreateAccountWithUserGuid', env),
    );
  }

  if (
    url.includes('/registration/') &&
    url.includes('/token') &&
    req.method === 'GET' &&
    isEndpointEnabled('CheckRegistrationToken', env)
  ) {
    return buildMockResult(
      'CheckRegistrationToken',
      'default',
      { customerGuid: 'mock-guid-123', loanId: 'mock-loan-456', username: 'user@example.com', isActive: false },
      getEndpointDelay('CheckRegistrationToken', env),
    );
  }

  if (url.includes('/registration') && req.method === 'POST' && isEndpointEnabled('ConfirmRegistration', env)) {
    return buildMockResult(
      'ConfirmRegistration',
      'success',
      { success: true, email: 'user@example.com', loanId: 'mock-loan-456', borrowerId: 'mock-borrower-789' },
      getEndpointDelay('ConfirmRegistration', env),
    );
  }

  if (url.includes('b2c-feature') && req.method === 'GET' && isEndpointEnabled('GetB2CFeatureFlag', env)) {
    return buildMockResult(
      'GetB2CFeatureFlag',
      'default',
      { isEnabled: true },
      getEndpointDelay('GetB2CFeatureFlag', env),
    );
  }

  if (
    url.includes('/origination/') &&
    url.includes('/profile') &&
    req.method === 'GET' &&
    isEndpointEnabled('LoadOriginationUserProfile', env)
  ) {
    return buildMockResult(
      'LoadOriginationUserProfile',
      'default',
      {
        firstName: 'John',
        lastName: 'Doe',
        email: 'john.doe@example.com',
        phone: '555-123-4567',
      },
      getEndpointDelay('LoadOriginationUserProfile', env),
    );
  }

  if (
    url.includes('/origination/') &&
    url.includes('/profile') &&
    req.method === 'PUT' &&
    isEndpointEnabled('UpdateOriginationProfile', env)
  ) {
    return buildMockResult(
      'UpdateOriginationProfile',
      'success',
      { success: true },
      getEndpointDelay('UpdateOriginationProfile', env),
    );
  }

  if (url.includes('/user/loans') && req.method === 'GET' && isEndpointEnabled('GetUserLoans', env)) {
    return buildMockResult('GetUserLoans', 'default', { loans: [] }, getEndpointDelay('GetUserLoans', env));
  }

  if (
    url.includes('/financial-institutions') &&
    url.includes('search') &&
    req.method === 'GET' &&
    isEndpointEnabled('SearchFinancialInstitutions', env)
  ) {
    return buildMockResult(
      'SearchFinancialInstitutions',
      'default',
      { institutions: [] },
      getEndpointDelay('SearchFinancialInstitutions', env),
    );
  }

  if (
    url.match(/\/financial-institutions\/[^/]+$/) &&
    req.method === 'GET' &&
    isEndpointEnabled('GetFinancialInstitution', env)
  ) {
    return buildMockResult(
      'GetFinancialInstitution',
      'default',
      { institution: null },
      getEndpointDelay('GetFinancialInstitution', env),
    );
  }

  if (
    url.includes('/financial-institutions/featured') &&
    req.method === 'GET' &&
    isEndpointEnabled('GetFeaturedInstitutions', env)
  ) {
    return buildMockResult(
      'GetFeaturedInstitutions',
      'default',
      { institutions: [] },
      getEndpointDelay('GetFeaturedInstitutions', env),
    );
  }

  if (url.includes('/application/submit') && req.method === 'POST' && isEndpointEnabled('SubmitApplication', env)) {
    return buildMockResult(
      'SubmitApplication',
      'success',
      { applicationId: 'mock-app-' + Date.now(), success: true },
      getEndpointDelay('SubmitApplication', env),
    );
  }

  if (url.includes('/api/v1/user/email') && req.method === 'GET' && isEndpointEnabled('MlaCheckEmailStatus', env)) {
    const scenario = getEndpointScenario('MlaCheckEmailStatus', env, 'active');
    const response =
      mlaCheckEmailStatusMock[scenario as keyof typeof mlaCheckEmailStatusMock] || mlaCheckEmailStatusMock['active'];
    return buildMockResult('MlaCheckEmailStatus', scenario, response, getEndpointDelay('MlaCheckEmailStatus', env));
  }

  if (
    url.includes('/api/v1/password') &&
    !url.includes('/api/v1/password/token') &&
    req.method === 'POST' &&
    isEndpointEnabled('MlaSendPasswordReset', env)
  ) {
    const scenario = getEndpointScenario('MlaSendPasswordReset', env, 'success');
    const response =
      mlaSendPasswordResetMock[scenario as keyof typeof mlaSendPasswordResetMock] ||
      mlaSendPasswordResetMock['success'];
    return buildMockResult(
      'MlaSendPasswordReset',
      scenario,
      response,
      getEndpointDelay('MlaSendPasswordReset', env),
    );
  }

  if (url.includes('/api/v1/password/token') && req.method === 'PATCH' && isEndpointEnabled('MlaUpdatePasswordWithToken', env)) {
    const scenario = getEndpointScenario('MlaUpdatePasswordWithToken', env, 'success');
    const response =
      mlaUpdatePasswordWithTokenMock[scenario as keyof typeof mlaUpdatePasswordWithTokenMock] ||
      mlaUpdatePasswordWithTokenMock['success'];
    return buildMockResult(
      'MlaUpdatePasswordWithToken',
      scenario,
      response,
      getEndpointDelay('MlaUpdatePasswordWithToken', env),
    );
  }

  if (env.mockConfig?.enabled && env.mockConfig?.activeScenario) {
    const isApiCall =
      url.includes('/api/') ||
      url.includes('/v1/') ||
      url.includes('/v2/') ||
      url.includes('loandepot') ||
      url.includes('ServicingLINC') ||
      url.includes('MelloServ') ||
      url.includes('mla-publicapi') ||
      url.includes('cp-public');

    if (isApiCall) {
      // eslint-disable-next-line no-console
      console.warn(
        `[MockInterceptor] AUTO-MOCKING unhandled ${req.method} ${url} - Consider adding explicit mock for this endpoint`,
      );

      if (req.method === 'GET') {
        return buildMockResult('AutoMocked', 'default', { success: true, data: null }, 0);
      } else if (req.method === 'POST' || req.method === 'PUT' || req.method === 'PATCH') {
        return buildMockResult('AutoMocked', 'default', { success: true, id: 'mock-' + Date.now() }, 0);
      } else if (req.method === 'DELETE') {
        return buildMockResult('AutoMocked', 'default', { success: true }, 0);
      }
    }
  }

  return null;
}
